"use client";

import { useId } from "react";
import { Button } from "../../../elements/button/button";
import { TextD2C } from "../../../elements/text-d2c/text";
import { cn } from "../../../lib/utils";
import { mainNavigationClasses } from "../main-navigation.constants";
import type { MainNavigationColumn } from "../main-navigation.types";
import { getNavigationTrackingAttributes } from "../navigation-tracking";

export interface NavigationColumnProps {
  column: MainNavigationColumn;
  /** Primary columns form the first mega menu row, secondary ones the groups below it. */
  variant: "primary" | "secondary";
  onLinkClick?: () => void;
}

/**
 * Mega menu column with its level 2 entries. Level 3 entries are only shown
 * in the mobile navigation because the desktop design has no place for them.
 */
export function NavigationColumn(props: Readonly<NavigationColumnProps>) {
  const headlineId = useId();

  return (
    <div className="flex h-full flex-col items-start">
      {props.column.headline ? (
        <TextD2C id={headlineId} style="uppercase" tagName="p" variant="h6">
          {props.column.headline}
        </TextD2C>
      ) : null}
      <ul
        aria-labelledby={props.column.headline ? headlineId : undefined}
        className={cn(
          "flex flex-col",
          props.column.headline && "mt-2",
          props.variant === "primary" ? "gap-4" : "gap-3"
        )}
      >
        {props.column.items.map((item) => (
          <li key={item.id}>
            <Button
              className={mainNavigationClasses.link}
              {...getNavigationTrackingAttributes(item, 2)}
              href={item.href}
              mode="D2C"
              onClick={props.onLinkClick}
              target={item.target}
              unstyled={true}
            >
              {item.label}
            </Button>
          </li>
        ))}
      </ul>
      {props.column.cta ? (
        // Pushed to the bottom so the CTAs of a row share one baseline.
        <div className="mt-auto pt-8">
          <Button
            className={mainNavigationClasses.cta}
            href={props.column.cta.href}
            mode="D2C"
            onClick={props.onLinkClick}
            target={props.column.cta.target}
            unstyled={true}
          >
            {props.column.cta.label}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
